import { types } from 'sass/types/legacy/function';
import {
  LargeAdvScheduleGifsUrl,
  SmallAdvScheduleGifsUrl,
} from '../../assets/HowToImages/HowtoCreateAdvSchedule/advScheduleUrls';
export enum HowToStepsImageFolder {
  EditReportingPrefsImages = 'EditReportingPrefsImages',
  CommonAccessSitesImages = 'CommonAccessSitesImages',
  HowToAccessLeoImages = 'HowToAccessLeoImages',
  HowToAccessTerminalModeImages = 'HowToAccessTerminalModeImages',
  DropDownAndPorts = 'dropDownAndPorts',
}

export type Url = {
  large: string;
  small: string;
};

export type Image = {
  filename?: string;
  folderName?: HowToStepsImageFolder;
  alt: string;
  hasZoomSrc?: boolean;
  url?: Url;
};

export type HowToStep = {
  title: string;
  important?: string;
  body: Array<string>;
  images: Array<Image>;
};

export type HowToStepsGroup = {
  howToSteps: Array<HowToStep>;
};

// common in between small and large devices
export const CommonHowToEditReportingSteps: Array<HowToStep> = [
  {
    title: 'Open the Menu on the Left',
    body: [
      "If it isn't already, open the menu on the left side of the screen.",
      'Look for the hamburger menu button on the top left of the screen.',
    ],
    images: [
      {
        filename: 'step1.gif',
        folderName: HowToStepsImageFolder.EditReportingPrefsImages,
        alt: 'Animation showing how to open the left-hand menu by tapping the hamburger button at the top left of the screen.',
      },
    ],
  },
  {
    title: 'Go to User Account',
    body: [
      'You will see a list of options in the menu on the left.',
      "In the menu, look for the option called 'User Account' and click on it.",
    ],
    images: [
      {
        filename: 'step2.gif',
        folderName: HowToStepsImageFolder.EditReportingPrefsImages,
        alt: "Animation showing how to navigate the left menu and click on the 'User Account' option.",
      },
    ],
  },
  {
    title: 'Go to Sites Tab in User Account Page',
    body: [
      "You will see the user account page, and you will be inside the 'Preferences' tab.",
      "In the list of tabs, look for the 'Sites' tab.",
      "Click on the 'Sites' Tab.",
    ],
    images: [
      {
        filename: 'step3.gif',
        folderName: HowToStepsImageFolder.EditReportingPrefsImages,
        alt: "Animation showing how to switch from the 'Preferences' tab to the 'Sites' tab on the User Account page.",
      },
    ],
  },
  {
    title: 'The Sites Tab',
    body: [
      "The page in the 'Sites Tab' will look like the following image.",
      "Scroll through the list and look for the site you would like to change notifications for. In the example image, we will be editing the 'EXAMPLES' site.",
    ],
    images: [
      {
        filename: 'step4.gif',
        folderName: HowToStepsImageFolder.EditReportingPrefsImages,
        alt: "Animation showing the 'Sites' tab with a list of sites and scrolling to find the 'EXAMPLES' site for editing.",
      },
    ],
  },
  {
    title: 'Account Sites',
    body: [
      'Click on the site you want to expand the menu.',
      'In the menu, you will see different options for email settings.',
      'Click on the slider button to enable/disable the emails that are sent to you.',
      "After you've made some changes, don't forget to hit the 'UPDATE' button on the bottom right.",
    ],
    images: [
      {
        filename: 'step5.gif',
        folderName: HowToStepsImageFolder.EditReportingPrefsImages,
        alt: 'image',
      },
    ],
  },
  {
    title: 'On a Successful Save',
    body: [
      'A green notification banner will appear on the top right of your screen notifying that the save was successful.',
      "You may click 'x' in the top-right of the banner to dismiss the notification.",
    ],
    images: [
      {
        filename: 'step6.gif',
        folderName: HowToStepsImageFolder.EditReportingPrefsImages,
        alt: "Animation showing how to expand a site’s menu, adjust email settings using slider buttons, and click the 'UPDATE' button to save changes.",
      },
    ],
  },
  {
    title: 'No User Options Available',
    body: [
      'If no options are displayed, you may not have the necessary permissions to modify your email notification settings.',
      'Please contact your site manager if you need to request access permissions.',
    ],
    images: [
      {
        filename: 'step7.png',
        folderName: HowToStepsImageFolder.EditReportingPrefsImages,
        alt: 'Screenshot showing no user options available for modifying email notification settings.',
      },
    ],
  },
  {
    title: 'Tutorial Complete',
    body: [
      'You’ve finished the tutorial for editing your reporting preferences.',
      'If you need to review this tutorial again, click the question mark icon in the top right corner of the screen.',
    ],
    images: [
      {
        filename: 'step8.gif',
        folderName: HowToStepsImageFolder.EditReportingPrefsImages,
        alt: 'Animation showing the completion of the tutorial and how to access it again by clicking the question mark icon in the top right corner.',
      },
    ],
  },
];

// common in between small and large devices
export const CommonAccessSitesSteps: Array<HowToStep> = [
  {
    title: 'Open the Menu on the Left',
    body: [
      "If it isn't already, open the menu on the left side of the screen.",
      'Look for the hamburger menu button on the top left of the screen.',
    ],
    images: [
      {
        filename: 'step1.gif',
        folderName: HowToStepsImageFolder.CommonAccessSitesImages,
        alt: 'Animation showing how to open the left-hand menu by tapping the hamburger button at the top left of the screen.',
      },
    ],
  },
  {
    title: 'Go to Sites Page',
    body: [
      'You will see a list of options in the menu on the left.',
      "In the menu, look for the option called 'Sites' and click on it.",
    ],
    images: [
      {
        filename: 'step2.gif',
        folderName: HowToStepsImageFolder.CommonAccessSitesImages,
        alt: "Animation showing how to navigate the left menu and click on the 'Sites' option.",
      },
    ],
  },
  {
    title: 'Find and Expand Your Site',
    body: [
      'A list of chains you have access to will appear.',
      'Find the chain that contains the site you want to access.',
      'Within the chain list, locate your specific site and click on it to expand its menu.',
      "In this example, the 'LEO5' site under the 'HLC' chain was clicked.",
    ],
    images: [
      {
        filename: 'step3.gif',
        folderName: HowToStepsImageFolder.CommonAccessSitesImages,
        alt: "Expanding a site's menu by selecting 'LEO5' from the HLC chain.",
      },
    ],
  },
];

// common in between small and large devices
export const CommonHowToAccessLeoSteps: Array<HowToStep> = [
  ...CommonAccessSitesSteps,
  {
    title: 'Connect to the LEO Facility Manager',
    body: [
      "In the expanded site menu, locate the option labeled 'LEO Panel View'.",
      "Click on 'LEO Panel View' and wait a few moments while it connects.",
    ],
    images: [
      {
        filename: 'step4.gif',
        folderName: HowToStepsImageFolder.HowToAccessLeoImages,
        alt: "Selecting the 'LEO Panel View' option from the expanded site menu and connecting.",
      },
    ],
  },
];

// common in between small and large devices
export const CommonHowToAccessTerminalModeSteps: Array<HowToStep> = [
  ...CommonAccessSitesSteps,
  {
    title: 'The Controller Menu',
    body: [
      "In the expanded site menu, locate the option labeled 'Controllers'.",
      "Click on the 'Controllers' option to expand a menu of controllers for that site.",
    ],
    images: [
      {
        filename: 'step4.gif',
        folderName: HowToStepsImageFolder.HowToAccessTerminalModeImages,
        alt: 'Image',
      },
    ],
  },
  {
    title: 'Find the Controller',
    body: [
      'From the menu of controllers, click on the controller you wish to connect to.',
      "This will bring you to the controller's page. You will see the controller's applications in the main page.",
      'In this example, we clicked on the E2 named RACK B.',
    ],
    images: [
      {
        filename: 'step5.gif',
        folderName: HowToStepsImageFolder.HowToAccessTerminalModeImages,
        alt: 'Image',
      },
    ],
  },
  {
    title: 'Connect to the Controller',
    body: [
      "Look for the green button on the main page called 'Terminal Mode'.",
      "Click on the 'Terminal Mode' button and wait a few moments while it connects.",
    ],
    images: [
      {
        filename: 'step6.gif',
        folderName: HowToStepsImageFolder.HowToAccessTerminalModeImages,
        alt: 'Image',
      },
    ],
  },
];

export const SmallDeviceHowToEditReportingSteps: Array<HowToStep> = [
  ...CommonHowToEditReportingSteps,
];

export const LargeDeviceHowToEditReportingSteps: Array<HowToStep> = [
  ...CommonHowToEditReportingSteps,
];

export const SmallDeviceHowToAccessLeoSteps: Array<HowToStep> = [
  ...CommonHowToAccessLeoSteps,
];

export const LargeDeviceHowToAccessLeoSteps: Array<HowToStep> = [
  ...CommonHowToAccessLeoSteps,
];

export const SmallDeviceHowToAccessTerminalModeSteps: Array<HowToStep> = [
  ...CommonHowToAccessTerminalModeSteps,
  {
    title: 'Action Menu Button',
    body: ['You will have your usual actions inside the action button menu.'],
    images: [
      {
        filename: 'step7.gif',
        folderName: HowToStepsImageFolder.HowToAccessTerminalModeImages,
        alt: 'Image',
      },
    ],
  },
];

export const LargeDeviceHowToAccessTerminalModeSteps: Array<HowToStep> = [
  ...CommonHowToAccessTerminalModeSteps,
];

export const CommonViewPortsFromLeo: Array<HowToStep> = [
  {
    title: 'You Can View Controller Ports',
    body: [
      "Look for the green 'Ports' Button.",
      'Click on it see a list of ports for the controllers.',
    ],
    images: [
      {
        filename: 'portsFromLeo.gif',
        folderName: HowToStepsImageFolder.DropDownAndPorts,
        alt: 'Image',
      },
    ],
  },
];

export const LargeDeviceViewPortsFromLeo: Array<HowToStep> = [
  ...CommonViewPortsFromLeo,
];

export const SmallDeviceViewPortsFromLeo: Array<HowToStep> = [
  ...CommonViewPortsFromLeo,
];

// common in between small and large devices
export const CommonViewPortsFromController: Array<HowToStep> = [
  {
    title: 'You Can View Ports',
    body: [
      "Look for the green 'Ports' Button.",
      'Click on it see a list of ports for the controllers.',
    ],
    images: [
      {
        filename: 'portsFromController.gif',
        folderName: HowToStepsImageFolder.DropDownAndPorts,
        alt: 'Image',
      },
    ],
  },
];

export const LargeDeviceViewPortsFromController: Array<HowToStep> = [
  ...CommonViewPortsFromController,
];

export const SmallDeviceViewPortsFromController: Array<HowToStep> = [
  ...CommonViewPortsFromController,
];

// common in between small and large devices
export const CommonViewDropDownFromLeo: Array<HowToStep> = [
  {
    title: 'You Can Connect to Other Controllers',
    body: [
      'Look for the drop down menu.',
      'Click on it see a list of controllers.',
      'From the list of controllers, click on the controller you wish to connect to.',
    ],
    images: [
      {
        filename: 'dropDownFromLeo.gif',
        folderName: HowToStepsImageFolder.DropDownAndPorts,
        alt: 'Image',
      },
    ],
  },
];

export const LargeDeviceViewDropDownFromLeo: Array<HowToStep> = [
  ...CommonViewDropDownFromLeo,
];

export const SmallDeviceViewDropDownFromLeo: Array<HowToStep> = [
  ...CommonViewDropDownFromLeo,
];

export const CommonViewDropDownFromController: Array<HowToStep> = [
  {
    title: 'You Can Connect to Other Controllers',
    body: [
      'Look for the drop down menu.',
      'Click on it see a list of controllers.',
      'From the list of controllers, click on the controller you wish to connect to.',
    ],
    images: [
      {
        filename: 'dropDownFromController.gif',
        folderName: HowToStepsImageFolder.DropDownAndPorts,
        alt: 'Image',
      },
    ],
  },
];

export const LargeDeviceViewDropDownFromController: Array<HowToStep> = [
  ...CommonViewDropDownFromController,
];

export const SmallDeviceViewDropDownFromController: Array<HowToStep> = [
  ...CommonViewDropDownFromController,
];

// common in between small and large devices
export const CommonHowToCreateAdvScheduleSteps: Array<HowToStep> = [
  {
    title: 'Open the Sites Menu',
    body: [
      "If it isn't already, open the menu on the left side of the screen.",
      "In the menu, look for the option called 'Sites' and click on it.",
    ],
    images: [
      {
        url: {
          large: `${LargeAdvScheduleGifsUrl}1.gif`,
          small: `${SmallAdvScheduleGifsUrl}1.gif`,
        },
        alt: "Clicking the 'Sites' option in the left-hand menu from the dashboard.",
      },
    ],
  },
  {
    title: 'Select Your Site',
    body: [
      'A list of chains and sites you have access to will appear.',
      'Find and click on the site you want to create an advanced schedule for.',
      'Once clicked, there will be an expanded menu where you can access the site "Settings" option.',
    ],
    images: [
      {
        url: {
          large: `${LargeAdvScheduleGifsUrl}2.gif`,
          small: `${SmallAdvScheduleGifsUrl}2.gif`,
        },
        alt: 'Select site setting.',
      },
    ],
  },
  {
    title: 'LEO Alert Tab',
    body: [
      "On the site's settings page, click on the 'LEO Alert' tab.",
      "Then click on 'Schedule'.",
    ],
    images: [
      {
        url: {
          large: `${LargeAdvScheduleGifsUrl}3.gif`,
          small: `${SmallAdvScheduleGifsUrl}3.gif`,
        },
        alt: "Clicking the 'LEO Alert' tab on the site settings page.",
      },
    ],
  },
  {
    title: 'Enable Advanced Schedule',
    body: [
      "In the top right, toggle on 'Advanced Schedule'.",
      'The schedule defaults to Standard. Toggle on Advanced Schedule to use the advanced scheduling options.',
    ],
    images: [
      {
        url: {
          large: `${LargeAdvScheduleGifsUrl}4.gif`,
          small: `${SmallAdvScheduleGifsUrl}4.gif`,
        },
        alt: "Toggling on the 'Advanced Schedule' switch.",
      },
    ],
  },
  {
    title: 'Add a New Schedule',
    body: ["Click the 'ADD SCHEDULE' button to create a schedule."],
    images: [
      {
        url: {
          large: `${LargeAdvScheduleGifsUrl}5.gif`,
          small: `${SmallAdvScheduleGifsUrl}5.gif`,
        },
        alt: "Click the 'ADD SCHEDULE' button.",
      },
    ],
  },
  {
    title: 'Fill in Schedule Details',
    body: [
      'Enter the schedule name and select your time zone.',
      "Click 'ADD' to save the schedule.",
    ],
    images: [
      {
        url: {
          large: `${LargeAdvScheduleGifsUrl}6.gif`,
          small: `${SmallAdvScheduleGifsUrl}6.gif`,
        },
        alt: "Click the 'ADD SCHEDULE' button.",
      },
    ],
  },
  {
    title: 'Create events for the schedule',
    body: [
      'Click on the Schedule that you want to add events to.',
      'Click "Edit" This will open the event editor where you can add and configure the event details.',
    ],
    images: [
      {
        url: {
          large: `${LargeAdvScheduleGifsUrl}7.gif`,
          small: `${SmallAdvScheduleGifsUrl}7.gif`,
        },
        alt: "Clicking 'EDIT' on the newly created schedule.",
      },
    ],
  },
  {
    title: 'Edit event',
    body: [
      'Click "Add EVENT" to create an event to the schedule.',
      'Select the Start Day, Start Time, End Day, and End Time from the dropdowns to define when the event begins and ends.',
      'Events can be configured for a single day or span multiple days.',
    ],
    images: [
      {
        url: {
          large: `${LargeAdvScheduleGifsUrl}8.gif`,
          small: `${SmallAdvScheduleGifsUrl}8.gif`,
        },
        alt: "Clicking 'EDIT' on the newly created schedule.",
      },
    ],
  },
  {
    title: 'Create 24/7 event',
    body: [
      '24/7 option allows the event to be active all day, every day.',
      "Click 'Save' to apply the changes to the schedule.",
    ],
    images: [
      {
        url: {
          large: `${LargeAdvScheduleGifsUrl}9.gif`,
          small: `${SmallAdvScheduleGifsUrl}9.gif`,
        },
        alt: "Clicking 'EDIT' on the newly created schedule.",
      },
    ],
  },
  {
    title: 'Assign Receivers to schedule',
    important: 'Do not skip this',
    body: [
      "Click the 'Select Receivers' dropdown at the bottom of the schedule to choose who should be notified.",
      "You can add more receivers to the dropdown list in the 'Receivers' tab.",
    ],
    images: [
      {
        url: {
          large: `${LargeAdvScheduleGifsUrl}10.gif`,
          small: `${SmallAdvScheduleGifsUrl}10.gif`,
        },
        alt: "Opening the 'Select Receivers' dropdown for the schedule.",
      },
    ],
  },
  {
    title: 'Advanced Schedule Created Successfully',
    body: [
      'The scheduled events will appear on the calendar at its designated time.',
      "Tooltips provide details about the scheduled event's days andtimes",
    ],
    images: [
      {
        url: {
          large: `${LargeAdvScheduleGifsUrl}11.gif`,
          small: `${SmallAdvScheduleGifsUrl}11.gif`,
        },
        alt: 'The completed advanced schedule with a receiver assigned, shown on the calendar.',
      },
    ],
  },
];

export const LargeDeviceHowToCreateAdvScheduleSteps: Array<HowToStep> = [
  ...CommonHowToCreateAdvScheduleSteps,
];

export const SmallDeviceHowToCreateAdvScheduleSteps: Array<HowToStep> = [
  ...CommonHowToCreateAdvScheduleSteps,
];
